<script setup lang="ts">
import { ref, watch } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import TrainDeprecatedModelDialog from '@/components/robvision/TrainDeprecatedModelDialog.vue';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

// Asked before every training: robots on RobFlow v8.2.0 or earlier only run
// the previous model generation, and users cannot be expected to know the
// model names. "Yes" leads to the deprecated-model card, "No" continues with
// the current model.
const isOpen = defineModel<boolean>('isOpen', { required: true });

// Per-generation entitlement from GET training-allowed. A generation that
// already succeeded since the last data upload cannot be picked again.
withDefaults(
  defineProps<{
    currentModelAllowed?: boolean;
    deprecatedModelAllowed?: boolean;
  }>(),
  { currentModelAllowed: true, deprecatedModelAllowed: true },
);

const emit = defineEmits<{
  current: [];
  deprecated: [resolutionAngle: number];
}>();

const deprecatedOpen = ref(false);

// Closing the deprecated card without confirming closes the whole choice.
watch(deprecatedOpen, (open) => {
  if (!open) {
    isOpen.value = false;
  }
});

const chooseCurrent = () => {
  isOpen.value = false;
  emit('current');
};

const chooseDeprecated = () => {
  deprecatedOpen.value = true;
};

const confirmDeprecated = (resolutionAngle: number) => {
  isOpen.value = false;
  emit('deprecated', resolutionAngle);
};
</script>

<template>
  <v-dialog v-model="isOpen" max-width="560">
    <v-card v-if="!deprecatedOpen">
      <v-card-title class="d-flex align-center">
        <v-icon
          icon="mdi-help-circle-outline"
          color="primary"
          size="small"
          class="mr-2"
        />
        {{ $gettext('Which robot will run the model?') }}
      </v-card-title>
      <v-card-text>
        <p>
          {{
            $gettext(
              'Do you want to use the model on a robot with RobFlow v8.2.0 or earlier?',
            )
          }}
        </p>
        <p class="text-medium-emphasis mb-0">
          {{
            $gettext(
              'Those robots only run the previous model generation. Newer robots use the current model.',
            )
          }}
        </p>
        <p
          v-if="!currentModelAllowed || !deprecatedModelAllowed"
          class="text-medium-emphasis mt-3 mb-0"
        >
          {{
            $gettext(
              'A model generation already trained on the current data needs new labeled data before it can be trained again.',
            )
          }}
        </p>
      </v-card-text>
      <v-card-actions>
        <r-btn variant="outlined" @click="isOpen = false">
          {{ $gettext('Cancel') }}
        </r-btn>
        <v-spacer></v-spacer>
        <r-btn
          variant="outlined"
          :disabled="!deprecatedModelAllowed"
          @click="chooseDeprecated"
        >
          {{ $gettext('Yes, v8.2.0 or earlier') }}
        </r-btn>
        <r-btn
          variant="accent"
          :disabled="!currentModelAllowed"
          @click="chooseCurrent"
        >
          {{ $gettext('No, newer') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <TrainDeprecatedModelDialog
    v-model:isOpen="deprecatedOpen"
    @confirm="confirmDeprecated"
  />
</template>
